import * as React from "react";

import { Box, makeStyles, Theme, Typography } from "@material-ui/core";

import hero from "client/assets/sidebar-hero.png";
import { useBranding, useFeatureFlags } from "client/hooks";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";
import { BrandTitle } from "client/utils/branding";

const useStyles = makeStyles((theme: Theme) => {
  const { gray96 } = useNormalizedBrandPaletteValues(theme);

  return {
    root: {
      "& > *": {
        marginBottom: theme.spacing(4),
      },
      backgroundColor: gray96,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(4, 2.8, 2, 2.8),
    },
    sectionHeader: {
      fontSize: "0.9rem",
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(0.6),
    },
    sectionBody: {
      fontSize: "0.9rem",
    },
    heading: {
      fontWeight: theme.typography.fontWeightBold,
    },
    link: {
      color: "#000",
      opacity: 0.87,
    },
  };
});

const Section: React.FC<{ header: string; body: string }> = ({
  header,
  body,
}) => {
  const classes = useStyles();
  return (
    <Box>
      <Typography variant="body2" className={classes.sectionHeader}>
        {header}
      </Typography>
      <Typography className={classes.sectionBody} variant="body2">
        {body}
      </Typography>
    </Box>
  );
};

const SectionWithAuRegChanges: React.FC<{
  header: string;
  body: React.ReactNode;
}> = ({ header, body }) => {
  const classes = useStyles();
  return (
    <Box>
      <Typography variant="body2" className={classes.sectionHeader}>
        {header}
      </Typography>
      <Typography className={classes.sectionBody} variant="body2">
        {body}
      </Typography>
    </Box>
  );
};

const getSectionsWithBranding = (brandTitle: BrandTitle) => [
  {
    header: "New customers",
    body: `Our service gets people to try new places. On average, 30% of a store’s ${brandTitle} users are shopping there for the first time.`,
  },
  {
    header: "Higher order values",
    body: `Our global ${brandTitle} community buys more. Stores that welcome our members can boost average order values by up to 40%.`,
  },
  {
    header: "More conversion",
    body: `Our online shopping technology converts. ${brandTitle} can grow cart conversions by up to 22%—plus more repeat shoppers.`,
  },
];

const getSectionsWithBrandingWithAuRegChanges = (
  brandTitle: BrandTitle,
  linkClassName: string
) => [
  {
    header: "New customers",
    body: (
      <>
        Our service connects merchants with new shoppers. 65% of merchants
        report reaching new customers after offering {brandTitle} as a payment
        option.
        <br></br>
        <i>
          Source:{" "}
          <a
            className={linkClassName}
            href="https://afterpay-newsroom.yourcreative.com.au/wp-content/uploads/2024/06/AP0437-Mandala-Economic-Impact-Report_Final-3.6.2024.pdf"
            target="_blank"
            rel="noreferrer"
          >
            Afterpay&#39;s economic impact in Australia report, June 2024
          </a>
        </i>
      </>
    ),
  },
  {
    header: "Higher order values",
    body: (
      <>
        Our active customers spend significantly more. On average, {brandTitle}{" "}
        customers&#39; orders are 28% higher than non-
        {brandTitle} orders.
      </>
    ),
  },
  {
    header: "More conversion",
    body: (
      <>
        Our technology drives better conversion rates. Adding {brandTitle} can
        boost cart conversions by up to 30%, and increase frequency by 21%.
        <br></br>
        <i>
          Source:{" "}
          <a
            className={linkClassName}
            href="https://stripe.com/blog/testing-the-impact-of-buy-now-pay-later?trk=feed_main-feed-card_feed-article-content"
            target="_blank"
            rel="noreferrer"
          >
            Stripe external report, June 2024
          </a>
        </i>
      </>
    ),
  },
];

export const Sidebar: React.FC = () => {
  const classes = useStyles();
  const { brandTitle } = useBranding();
  const [{ merchantPortalAuRegUpdates }] = useFeatureFlags();

  return (
    <Box
      className={classes.root}
      display="flex"
      flexDirection="column"
      mt={{ xs: 5, md: 0 }}
    >
      <Box display="flex" justifyContent="center">
        <img src={hero} alt="girl listening to music" />
      </Box>
      <Box>
        <Typography className={classes.heading} variant="h6">
          How {brandTitle} pays off
        </Typography>
      </Box>
      {merchantPortalAuRegUpdates
        ? getSectionsWithBrandingWithAuRegChanges(
            brandTitle,
            classes.link
          ).map(({ header, body }) => (
            <SectionWithAuRegChanges key={header} header={header} body={body} />
          ))
        : getSectionsWithBranding(brandTitle).map(({ header, body }) => (
            <Section key={header} header={header} body={body} />
          ))}
    </Box>
  );
};
